import {
  BankAccountDetails,
  BankAccountType,
  CheckTaxRefundDeliveryMethod,
  CreateTaxRefundMethodRequest,
  DirectDepositTaxRefundDeliveryMethod,
  FutureTaxCreditTaxRefundDeliveryMethod,
  TaxRefundDeliveryMethod,
  TaxRefundType,
  UpdateTaxRefundMethodRequest,
} from '@taxfyle/api-internal/internal/tax_refund_methods_pb'
import { PhysicalAddress } from '@taxfyle/api-internal/shared/types/physical_address_pb'
import { Id } from 'types/definitions'
import { mapPhysicalAddressToProto } from '../individual-info/individualProfileGrpcDtoMapper'

export type DeliveryMethodInput =
  | { kind: 'check'; mailingAddress?: PhysicalAddress.AsObject }
  | { kind: 'directDeposit'; bankAccount: BankAccountDetails.AsObject }
  | { kind: 'futureTaxCredit' }

export interface CreateTaxRefundMethodInput {
  workspaceId: Id
  ownerUserId: Id
  refundType: 'personal' | 'business'
  deliveryMethod: DeliveryMethodInput
}

export interface UpdateTaxRefundMethodInput {
  id: Id
  deliveryMethod: DeliveryMethodInput
}

export function mapBankAccountToProto(
  input: BankAccountDetails.AsObject,
): BankAccountDetails {
  return new BankAccountDetails()
    .setAccountType(input.accountType)
    .setAccountNumber(input.accountNumber)
    .setRoutingNumber(input.routingNumber)
    .setBankName(input.bankName)
}

export function mapDeliveryMethodToProto(
  input: DeliveryMethodInput,
): TaxRefundDeliveryMethod {
  const proto = new TaxRefundDeliveryMethod()
  switch (input.kind) {
    case 'check': {
      const check = new CheckTaxRefundDeliveryMethod()
      if (input.mailingAddress) {
        check.setMailingAddress(mapPhysicalAddressToProto(input.mailingAddress))
      }
      proto.setCheck(check)
      return proto
    }
    case 'directDeposit': {
      const directDeposit =
        new DirectDepositTaxRefundDeliveryMethod().setBankAccount(
          mapBankAccountToProto(input.bankAccount),
        )
      proto.setDirectDeposit(directDeposit)
      return proto
    }
    case 'futureTaxCredit': {
      proto.setFutureTaxCredit(new FutureTaxCreditTaxRefundDeliveryMethod())
      return proto
    }
  }
}

export function mapCreateTaxRefundMethodRequestToProto(
  input: CreateTaxRefundMethodInput,
): CreateTaxRefundMethodRequest {
  return new CreateTaxRefundMethodRequest()
    .setWorkspaceId(Id.assert(input.workspaceId))
    .setOwnerUserId(Id.assert(input.ownerUserId))
    .setRefundType(
      input.refundType === 'personal'
        ? TaxRefundType.PERSONAL
        : TaxRefundType.BUSINESS,
    )
    .setDeliveryMethod(mapDeliveryMethodToProto(input.deliveryMethod))
}

export function mapUpdateTaxRefundMethodRequestToProto(
  input: UpdateTaxRefundMethodInput,
): UpdateTaxRefundMethodRequest {
  return new UpdateTaxRefundMethodRequest()
    .setId(Id.assert(input.id))
    .setDeliveryMethod(mapDeliveryMethodToProto(input.deliveryMethod))
}

export function bankAccountTypeFromString(
  value: string,
): BankAccountType {
  switch (value) {
    case 'Checking':
      return BankAccountType.CHECKING
    case 'Savings':
      return BankAccountType.SAVINGS
    default:
      return BankAccountType.BANK_ACCOUNT_TYPE_UNSPECIFIED
  }
}

export function bankAccountTypeToString(
  value: BankAccountType,
): 'Checking' | 'Savings' | '' {
  switch (value) {
    case BankAccountType.CHECKING:
      return 'Checking'
    case BankAccountType.SAVINGS:
      return 'Savings'
    default:
      return ''
  }
}
